import React, { useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import { clearStorageTransactionPrice } from 'Util/_HumanWritten/CartPriceSessionStorage';
import { getOldEventBookingWizardData } from 'Views/Components/_HumanWritten/BookingWizard/EventsBookingWizard/EventsBookingWizardData';
import { newAlertModal } from 'Views/Components/Modal/NewAlertModal';
import { ReservationTimer } from 'Views/Components/_HumanWritten/ReservationTimer/ReservationTimer';
import { discardAlterations } from 'Services/Api/_HumanWritten/BookingService/FerryTripBookingService';
import {
	clearEventBookingTransactionIdFromStorage,
	clearFerryBookingTransactionIdFromStorage,
	getEventBookingTransactionIdFromStorage,
	getFerryBookingTransactionIdFromStorage,
} from 'Services/Api/_HumanWritten/BookingService/BookingService';
import {
	clearAllEventBookingWizardDataFromStorage,
	clearAllFerryBookingWizardDataFromStorage,
} from 'Util/_HumanWritten/BookingWizard/BookingWizardUtils';
import { getOldWizardData } from '../BookingWizardDataStore';

export interface BookingTimerWrapProps {
	onTimerExpiry: () => void;
	isIpad: boolean;
	ticketsPageUrl: string;
	forEventBooking: boolean;
}

/**
 * The booking wizard's reservation countdown.
 *
 * The countdown itself — reading the server's expiry, ticking, and offering to extend under five
 * minutes — is <see cref="ReservationTimer" />, shared with check-in and the purchase modals. What
 * stays here is the part that is specific to the wizard: finding the transaction in browser storage,
 * and what running out of time means (clear the wizard's stored data, then send the customer back to
 * ticket selection, or discard the alteration and return them to their booking).
 *
 * `strip` on mobile, where it spans the width under the progress bar; `card` on desktop, where it
 * sits beside the back button.
 */
export function FerryBookingTimerWrap({
	onTimerExpiry,
	isIpad,
	ticketsPageUrl,
	forEventBooking,
}: BookingTimerWrapProps) {
	const navigate = useNavigate();
	const location = useLocation();
	const expiryHandledRef = useRef(false);

	const transactionId = forEventBooking
		? getEventBookingTransactionIdFromStorage()
		: getFerryBookingTransactionIdFromStorage();

	const handleExpiry = useCallback(() => {
		if (expiryHandledRef.current) {
			return;
		}
		expiryHandledRef.current = true;

		let isAlteration = false;
		let bookingId = '';

		if (!forEventBooking) {
			const oldWizardData = getOldWizardData();
			isAlteration = isNotNullOrUndefined(oldWizardData) && oldWizardData.wizardMode === 'ALTERATION';

			bookingId = oldWizardData?.bookingToEdit ?? '';
			if (isAlteration) {
				clearAllFerryBookingWizardDataFromStorage();
			} else {
				clearFerryBookingTransactionIdFromStorage();
			}
		} else {
			const oldWizardData = getOldEventBookingWizardData();

			bookingId = oldWizardData?.bookingToEditId ?? '';
			if (isAlteration) {
				clearAllEventBookingWizardDataFromStorage();
			} else {
				clearEventBookingTransactionIdFromStorage();
			}
		}

		clearStorageTransactionPrice();
		onTimerExpiry();

		newAlertModal('Session has expired', 'You have run out of time and you will need to start a new session', {
			cancelText: `Return to ${isAlteration ? 'booking' : 'ticket selection'}`,
			shouldCloseOnOverlayClick: false,
			shouldCloseOnEsc: false,
		}).then(() => {
			// If we are on the tickets page, navigating there will do nothing. Therefore, we need to
			// refresh the page in that scenario
			if (!isAlteration) {
				if (location.pathname === ticketsPageUrl) {
					window.location.reload();
				} else {
					navigate(ticketsPageUrl);
				}
			} else {
				discardAlterations(navigate, { bookingIds: [bookingId], systemExpired: true })
					.then(() => {
						navigate(`/bookings/${bookingId}`);
					})
					.catch(() => {
						navigate(`/bookings/${bookingId}`);
					});
			}
		});
	}, [forEventBooking, onTimerExpiry, ticketsPageUrl, location.pathname, navigate]);

	if (transactionId === null || transactionId === '') {
		return null;
	}

	return (
		<ReservationTimer
			transactionId={transactionId}
			onExpiry={handleExpiry}
			variant={isIpad ? 'banner' : 'pill'}
		/>
	);
}
